import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { Button, FormLayout, Modal, ModalBody, ModalFooter, Text } from '@customerio/pluma-components/react';
import { Form, TextArea, useForm } from '@/components/form';
import { authStore } from '@/services/auth/store';
import { useStore } from '@nanostores/react';
import { z } from 'zod';
import { useSubmitFeedback } from '../hooks/use-submit-feedback';

interface FeatureFeedbackModalProps {
  feature: string;
  initialFeedback?: string;
}

export function FeatureFeedbackModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<FeatureFeedbackModalProps>) {
  const auth = useStore(authStore);
  const submitFeedback = useSubmitFeedback();

  const form = useForm({
    defaultValues: {
      feedback: data.initialFeedback ?? '',
    },
    validators: {
      onChange: z.object({
        feedback: z.string().min(1, 'Feedback is required'),
      }),
    },
    async onSubmit(values) {
      try {
        await submitFeedback({
          ...values.value,
          source: `${data.feature} Feedback`,
          praise: values.value.feedback,
          criticism: '',
          replyTo: auth.user?.email ?? '',
        });

        onConfirm(values.value);
      } catch {
        // noop
      }
    },
  });

  const handleClose = () => {
    onClose();
  };

  const isSubmitDisabled = form.state.isValid !== true || form.state.isSubmitting;

  if (auth.account == null || auth.user == null) {
    return null;
  }

  return (
    <Modal
      state={modalState}
      onClose={handleClose}
      title={`${data.feature} Feedback`}
      size="md"
      shouldCloseOnOverlayClick={false}
    >
      <ModalBody>
        <Form api={form} id="feature-feedback-form">
          <Text mb="200" color="subtle">
            Let us know how we can improve {data.feature.toLowerCase()}! Your feedback will go directly to the team
            working on this feature.
          </Text>

          <FormLayout mt="200">
            <TextArea
              api={form}
              name="feedback"
              label="Share your feedback"
              placeholder="Share your feedback..."
              minRows={3}
              maxRows={8}
              id="feature-feedback-textarea"
            />
          </FormLayout>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="subtle" onClick={handleClose} isDisabled={form.state.isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" form="feature-feedback-form" type="submit" isDisabled={isSubmitDisabled}>
          Submit feedback
        </Button>
      </ModalFooter>
    </Modal>
  );
}
